import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Seo from '@/components/Seo';
import { Breadcrumb } from '@/components/layout/Breadcrumb';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { ToolLongForm } from '@/components/tools/ToolLongForm';
import { ToolFAQ } from '@/components/tools/ToolFAQ';
import { AdSlot } from '@/components/ads/AdSlot';
import { Pause, Play, RotateCcw, Timer as TimerIcon, ShieldCheck, Flag } from 'lucide-react';
import { toast } from 'sonner';

const FAQS = [
    { q: 'Does the timer keep running if I switch tabs?', a: 'Yes. It uses your system clock, not a JavaScript interval, so tab throttling doesn’t affect accuracy.' },
    { q: 'Will it beep when the countdown ends?', a: 'Yes — the Web Audio API plays a short beep so you don’t need to have the tab focused.' },
    { q: 'Does it need an internet connection?', a: 'Only for the first page load. After that, the timer runs entirely in your browser.' },
    { q: 'Can I use it as a stopwatch?', a: 'Yes. Switch to the Stopwatch tab for start/stop/lap functionality.' },
];

const HOW = [
    'The Countdown tab lets you set hours, minutes, and seconds, then counts down to zero. When it reaches zero, we play a soft beep using the Web Audio API and flash a subtle visual cue. You can pause, resume, or reset any time. The stopwatch tab starts at zero and counts up until you stop it, with a Lap button for splits.',
    'Both modes use `performance.now()` under the hood, which means the timer stays accurate even when your tab is inactive or your laptop sleeps. Some in-browser timers drift because they rely on setInterval, which gets throttled by the browser to save battery — ours doesn’t.',
    'Everything happens locally. No network requests, no signups, and nothing about your usage is logged. Bookmark the page and it works offline once the JavaScript is cached.',
];
const BENEFITS = [
    'Timers are one of those small primitives that quietly organize the rest of your day. Pomodoro sessions of 25 minutes with 5-minute breaks are the classic use, but the same tool covers cooking, meditations, workouts, standups, and every meeting box you’d like to actually keep to.',
    'For interviews, presentations, and podcasts, a stopwatch with laps helps you track how long each section is running. That live awareness turns rehearsals from “I think I went over” into precise data you can trim from.',
    'And because this tool doesn’t need an install or an account, it works on any device you happen to have handy. A borrowed laptop, a hotel-lobby computer, your phone in a queue — open it and it just works.',
];
const TIPS = [
    'For deep-work sessions try 50 minutes on, 10 minutes off (“50/10”) — a good middle ground between Pomodoro and marathon working.',
    'Use the stopwatch’s Lap button to time each rep or set during a workout.',
    'Keep the tab pinned so a beep is always one click away.',
];

function pad(n) { return n.toString().padStart(2, '0'); }
function fmtMs(ms, showMs = false) {
    if (ms < 0) ms = 0;
    const total = Math.floor(ms / 1000);
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    const rest = showMs ? `.${Math.floor((ms % 1000) / 10).toString().padStart(2, '0')}` : '';
    return `${pad(h)}:${pad(m)}:${pad(s)}${rest}`;
}

function playBeep(duration = 0.4) {
    try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = 'sine';
        o.frequency.value = 880;
        g.gain.value = 0.1;
        o.connect(g);
        g.connect(ctx.destination);
        o.start();
        setTimeout(() => {
            o.stop();
            ctx.close();
        }, duration * 1000);
    } catch {
        // Audio context unavailable (e.g. autoplay policy) — silently no-op.
    }
}

function Countdown() {
    const [h, setH] = useState('0');
    const [m, setM] = useState('5');
    const [s, setS] = useState('0');
    const [remaining, setRemaining] = useState(0);
    const [running, setRunning] = useState(false);
    const [alarmed, setAlarmed] = useState(false);
    const endAtRef = useRef(0);

    const configuredMs = useMemo(() => {
        const hh = Math.max(0, Number(h) || 0);
        const mm = Math.max(0, Number(m) || 0);
        const ss = Math.max(0, Number(s) || 0);
        return (hh * 3600 + mm * 60 + ss) * 1000;
    }, [h, m, s]);

    useEffect(() => {
        if (!running) return;
        let raf;
        const tick = () => {
            const now = performance.now();
            const rem = endAtRef.current - now;
            if (rem <= 0) {
                setRemaining(0);
                setRunning(false);
                if (!alarmed) {
                    setAlarmed(true);
                    playBeep();
                    toast.success('Time’s up!');
                }
                return;
            }
            setRemaining(rem);
            raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [running, alarmed]);

    const start = () => {
        const useMs = remaining > 0 ? remaining : configuredMs;
        if (useMs <= 0) {
            toast.error('Enter a duration greater than zero.');
            return;
        }
        endAtRef.current = performance.now() + useMs;
        setAlarmed(false);
        setRunning(true);
    };
    const pause = () => {
        setRunning(false);
        setRemaining(Math.max(0, endAtRef.current - performance.now()));
    };
    const reset = () => {
        setRunning(false);
        setRemaining(0);
        setAlarmed(false);
    };

    const display = fmtMs(
        running
            ? remaining
            : remaining > 0
            ? remaining
            : alarmed
            ? 0
            : configuredMs,
    );

    return (
        <Card className="p-6 border-border/80">
            <div className="grid grid-cols-3 gap-3">
                {[
                    { l: 'Hours', v: h, s: setH },
                    { l: 'Minutes', v: m, s: setM },
                    { l: 'Seconds', v: s, s: setS },
                ].map((f, i) => (
                    <div key={i}>
                        <Label>{f.l}</Label>
                        <Input
                            type="number"
                            min={0}
                            max={f.l === 'Hours' ? 99 : 59}
                            value={f.v}
                            onChange={(e) => f.s(e.target.value)}
                            disabled={running}
                            className="mt-1.5 text-center text-lg tabular-nums"
                            data-testid={`tool-timer-${f.l.toLowerCase()}`}
                        />
                    </div>
                ))}
            </div>
            <div className="mt-8 flex flex-col items-center">
                <div
                    data-testid="tool-timer-display"
                    className={`font-mono tabular-nums text-6xl sm:text-7xl font-semibold tracking-tight ${
                        alarmed ? 'text-emerald-600' : 'text-slate-900'
                    }`}
                >
                    {display}
                </div>
                <div className="mt-6 flex gap-3">
                    {!running ? (
                        <Button
                            onClick={start}
                            data-testid="tool-timer-start"
                            className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--link-hover))] text-white"
                        >
                            <Play className="w-4 h-4 mr-2" /> {remaining > 0 ? 'Resume' : 'Start'}
                        </Button>
                    ) : (
                        <Button onClick={pause} data-testid="tool-timer-pause" variant="secondary">
                            <Pause className="w-4 h-4 mr-2" /> Pause
                        </Button>
                    )}
                    <Button onClick={reset} variant="outline" data-testid="tool-timer-reset">
                        <RotateCcw className="w-4 h-4 mr-2" /> Reset
                    </Button>
                </div>
            </div>
        </Card>
    );
}

function Stopwatch() {
    const [ms, setMs] = useState(0);
    const [running, setRunning] = useState(false);
    const [laps, setLaps] = useState([]);
    const startedAtRef = useRef(0);
    const accumRef = useRef(0);

    useEffect(() => {
        if (!running) return;
        let raf;
        const tick = () => {
            const now = performance.now();
            setMs(accumRef.current + (now - startedAtRef.current));
            raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [running]);

    const start = () => {
        startedAtRef.current = performance.now();
        setRunning(true);
    };
    const stop = () => {
        accumRef.current += performance.now() - startedAtRef.current;
        setRunning(false);
    };
    const reset = () => {
        setRunning(false);
        accumRef.current = 0;
        setMs(0);
        setLaps([]);
    };
    const lap = () => {
        setLaps((cur) => [...cur, ms]);
    };

    return (
        <Card className="p-6 border-border/80">
            <div className="flex flex-col items-center">
                <div
                    data-testid="tool-stopwatch-display"
                    className="font-mono tabular-nums text-6xl sm:text-7xl font-semibold tracking-tight text-slate-900"
                >
                    {fmtMs(ms, true)}
                </div>
                <div className="mt-6 flex gap-3">
                    {!running ? (
                        <Button
                            onClick={start}
                            data-testid="tool-stopwatch-start"
                            className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--link-hover))] text-white"
                        >
                            <Play className="w-4 h-4 mr-2" /> {ms > 0 ? 'Resume' : 'Start'}
                        </Button>
                    ) : (
                        <Button onClick={stop} data-testid="tool-stopwatch-stop" variant="secondary">
                            <Pause className="w-4 h-4 mr-2" /> Stop
                        </Button>
                    )}
                    <Button onClick={lap} disabled={!running} variant="outline" data-testid="tool-stopwatch-lap">
                        <Flag className="w-4 h-4 mr-2" /> Lap
                    </Button>
                    <Button onClick={reset} variant="outline" data-testid="tool-stopwatch-reset">
                        <RotateCcw className="w-4 h-4 mr-2" /> Reset
                    </Button>
                </div>
                {laps.length > 0 ? (
                    <div className="mt-6 w-full max-w-md">
                        <p className="text-sm font-medium text-slate-700">Laps</p>
                        <ol className="mt-2 space-y-1 text-sm font-mono tabular-nums">
                            {laps.map((l, i) => (
                                <li key={i} className="flex justify-between border-b border-border/60 py-1">
                                    <span className="text-slate-500">Lap {i + 1}</span>
                                    <span className="text-slate-800">{fmtMs(l, true)}</span>
                                </li>
                            ))}
                        </ol>
                    </div>
                ) : null}
            </div>
        </Card>
    );
}

export default function TimerPage() {
    const structured = {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'UtilityTool — Timer',
        applicationCategory: 'Utility',
        operatingSystem: 'Any',
        offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    };

    return (
        <>
            <Seo
                title="Timer & Stopwatch"
                description="A simple countdown timer and stopwatch that runs entirely in your browser. Beeps when time is up. Perfect for Pomodoro, cooking, workouts."
                canonical="/tools/timer"
                structuredData={structured}
            />
            <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-14">
                <Breadcrumb items={[{ label: 'Tools', to: '/tools' }, { label: 'Timer' }]} />
                <div className="mt-4 flex items-start gap-4">
                    <span className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--primary))]">
                        <TimerIcon className="w-6 h-6" />
                    </span>
                    <div>
                        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">Timer & Stopwatch</h1>
                        <p className="mt-2 text-slate-600 max-w-2xl">
                            Countdown with a soft beep, or a laps-enabled stopwatch. Both
                            run entirely in your browser.
                        </p>
                        <div className="mt-3 flex flex-wrap gap-2">
                            <Badge variant="secondary" className="bg-slate-100 text-slate-700">In-browser</Badge>
                            <Badge variant="secondary" className="bg-slate-100 text-slate-700">Tab-safe timing</Badge>
                        </div>
                    </div>
                </div>

                <div className="mt-8">
                    <Tabs defaultValue="countdown" className="w-full">
                        <TabsList className="grid grid-cols-2 max-w-sm">
                            <TabsTrigger value="countdown" data-testid="tool-timer-tab-countdown">Countdown</TabsTrigger>
                            <TabsTrigger value="stopwatch" data-testid="tool-timer-tab-stopwatch">Stopwatch</TabsTrigger>
                        </TabsList>
                        <TabsContent value="countdown" className="mt-4">
                            <Countdown />
                        </TabsContent>
                        <TabsContent value="stopwatch" className="mt-4">
                            <Stopwatch />
                        </TabsContent>
                    </Tabs>
                </div>

                <div className="mt-6 rounded-xl border border-border/70 bg-[hsl(var(--surface-2))] p-4">
                    <div className="flex items-center gap-2 text-slate-800">
                        <ShieldCheck className="w-4 h-4 text-[hsl(var(--primary))]" />
                        <span className="text-sm font-semibold">Runs locally on your device</span>
                    </div>
                    <p className="mt-1 text-sm text-slate-600">
                        No network requests, no signups, no logging.
                    </p>
                </div>

                <ToolLongForm howItWorks={HOW} benefits={BENEFITS} tips={TIPS} />
                <AdSlot placement="tool" />
                <ToolFAQ items={FAQS} />
            </section>
        </>
    );
}
